import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import DetailTable from 'components/DetailTable';
import { ConfigItem } from 'components/DetailTable/types';
import LoadingScreen from 'components/LoadingScreen';
import { useAtomValue } from 'jotai';
import React, { Fragment, useEffect, useState } from 'react';
import { Row, Col, Button, ButtonGroup } from 'reactstrap';
import RepoInfoSummary from 'views/sast2/components/RepoInfoSummary';
import { HostType } from 'views/sast2/helper';
import { companyIdAtom } from 'views/state';

import { BuildLogsTable } from '../BuildLogsDisplayDialog';
import { fetchRepositoryDetail, processJsonData } from '../helpers';

type Props = {
  id: number;
};

const RepositoryDetailModalContent = ({ id }: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [data, setData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const companyId = useAtomValue(companyIdAtom);

  useEffect(() => {
    if (companyId && companyId !== -1) {
      fetchRepositoryDetail(id, companyId)
        .then((res) => {
          if (res) {
            const processedJson = processJsonData(res);
            setData(processedJson);
          }
        })
        .catch((err) => {
          throw err;
        })
        .finally(() => setIsLoading(false));
    }
  }, [id, companyId]);

  if (isLoading && !data) return <LoadingScreen />;

  const hostType: HostType = data['account']['host_type'];

  const generalTabConfig = [
    { name: 'Id', key: 'id', type: 'string' },
    { name: 'Name', key: 'name', type: 'string' },
    { name: 'Branch', key: 'branch', type: 'string' },
    hostType === HostType.Gitlab && {
      name: 'Group',
      key: 'group_name',
      type: 'string',
    },
    hostType === HostType.Gitlab && {
      name: 'Project Id',
      key: 'project_id',
      type: 'string',
    },
    hostType === HostType.Bitbucket && {
      name: 'Workspace',
      key: 'workspace',
      type: 'string',
    },
    (hostType === HostType.Azure || hostType === HostType.Github) && {
      name: 'Organization',
      key: 'organization',
      type: 'string',
    },
    hostType === HostType.Azure && {
      name: 'Project',
      key: 'project',
      type: 'string',
    },
    { name: 'Active', key: 'is_active', type: 'boolean' },
    { name: 'Status', key: 'status', type: 'string' },
    { name: 'UUID', key: 'uuid', type: 'string' },
    { name: 'URL', key: 'url', type: 'url' },
    {
      name: 'Last Reactivated By',
      key: 'reactivated_by',
      type: 'object',
      config: [
        { name: 'Name', key: 'fullname', type: 'string' },
        { name: 'Email', key: 'email', type: 'string' },
      ],
    },
    { name: 'Last Reactivated At', key: 'reactivated_at', type: 'date' },
    {
      name: 'Last Deactivated By',
      key: 'deactivated_by',
      type: 'object',
      config: [
        { name: 'Name', key: 'fullname', type: 'string' },
        { name: 'Email', key: 'email', type: 'string' },
      ],
    },
    { name: 'Last Deactivated At', key: 'deactivated_at', type: 'date' },
    { name: 'Code Updated At', key: 'last_commit_date', type: 'date' },
    {
      name: 'Last Scanned At',
      key: 'scan.last_sync_completed_at',
      type: 'date',
    },
    { name: 'Created At', key: 'created_at', type: 'date' },
    { name: 'Updated At', key: 'updated_at', type: 'date' },
  ].filter((item) => item !== null) as ConfigItem[];

  const accountTabConfig: ConfigItem[] = [
    { name: 'Id', key: 'account.id', type: 'string' },
    { name: 'Title', key: 'account.title', type: 'string' },
    { name: 'Type', key: 'account.host_type', type: 'string' },
    { name: 'Active', key: 'account.is_active', type: 'boolean' },
    {
      name: 'Added By',
      key: 'account.user',
      type: 'object',
      config: [
        { name: 'Name', key: 'fullname', type: 'string' },
        { name: 'Email', key: 'email', type: 'string' },
      ],
    },
  ];

  const scanTabConfig: ConfigItem[] = [
    { name: 'Status', key: 'status', type: 'string' },
    {
      name: 'Languages Scanned',
      key: 'languages.supported_languages',
      type: 'string',
    },
    {
      name: 'Languages Not Scanned',
      key: 'languages.unsupported_languages',
      type: 'string',
    },
    { name: 'Lines Of Code', key: 'scan.ncloc', type: 'string' },
    { name: 'Bugs', key: 'scan.bugs_count', type: 'string' },
    { name: 'Synced Bugs', key: 'scan.synced_bugs_count', type: 'string' },
    { name: 'Vulnerabilities', key: 'scan.vulnerabilities', type: 'string' },
    {
      name: 'Synced Vulnerabilities',
      key: 'scan.synced_vuln_count',
      type: 'string',
    },
    {
      name: 'Blocker Violations',
      key: 'scan.blocker_violations',
      type: 'string',
    },
    { name: 'New Violations', key: 'scan.new_violations', type: 'string' },
    // { name: "Manual Scan", key: "scan.is_manual_scan", type: "boolean" },
    { name: 'Last Triggered At', key: 'scan.job_triggered_at', type: 'date' },
    // { name: "Last Started At", key: "scan.job_started_at", type: "date" },
    { name: 'Last Ended At', key: 'scan.job_completed_at', type: 'date' },
    { name: 'Scan Duration', key: 'scan.scan_duration', type: 'string' },
    {
      name: 'Last Scanned At',
      key: 'scan.last_sync_completed_at',
      type: 'date',
    },
    {
      name: 'Status History',
      key: 'status_history',
      type: 'list',
      listConfig: {
        childNode: undefined,
        config: [
          { name: 'Date', key: 'date', type: 'date' },
          { name: 'Status', key: 'status', type: 'string' },
          { name: 'Description', key: 'description', type: 'string' },
        ],
      },
    },
  ];

  // tabs is the configuration for the tabs which are rendered
  const tabs = [
    {
      title: 'General Info',
      config: generalTabConfig,
      data: data,
    },
    {
      title: 'Account Info',
      config: accountTabConfig,
      data: data,
    },
    {
      title: 'Scan',
      config: scanTabConfig,
      data: data,
    },
  ];

  return (
    <CustomModalWrapper overflowY="hidden" id="repository-detail-wrapper">
      {data && (
        <Fragment>
          <Row>
            <Col sm="6" md="4" lg="3">
              <ButtonGroup
                vertical
                className="btn-group-toggle"
                style={{ maxWidth: '100%', width: '100%' }}
              >
                {tabs.map((tab, index) => (
                  <Button
                    key={`tab-btn-${index}`}
                    tag="label"
                    style={{
                      padding: '10px',
                      margin: '0px',
                      textAlign: 'left',
                    }}
                    color={activeTab === index ? 'primary' : 'secondary'}
                    size="sm"
                    title={tab.title}
                    onClick={() => setActiveTab(index)}
                  >
                    {tab.title}
                  </Button>
                ))}
                {data?.scan?.has_build_logs && (
                  <Button
                    tag="label"
                    style={{
                      padding: '10px',
                      margin: '0px',
                      textAlign: 'left',
                    }}
                    color={activeTab === tabs.length ? 'primary' : 'secondary'}
                    size="sm"
                    title="Scanner Errors"
                    onClick={() => setActiveTab(tabs.length)}
                  >
                    Scanner Errors
                  </Button>
                )}
              </ButtonGroup>
            </Col>
            <Col sm="12" md="8" lg="9">
              <CustomModalWrapper id="repository-detail-scroll">
                {tabs.map(
                  (tab, index) =>
                    activeTab === index && (
                      <>
                        <Row>
                          <Col sm="12">
                            {data ? (
                              <RepoInfoSummary
                                props={data}
                                description={undefined}
                                mode={'normal'}
                              />
                            ) : (
                              <></>
                            )}
                          </Col>
                        </Row>
                        <DetailTable
                          key={index}
                          data={tab.data}
                          config={tab.config}
                        />
                      </>
                    ),
                )}
                {data?.scan?.has_build_logs && activeTab === tabs.length && (
                  <>
                    <Row>
                      <Col sm="12">
                        {data ? (
                          <RepoInfoSummary
                            props={data}
                            description={undefined}
                            mode={'normal'}
                          />
                        ) : (
                          <></>
                        )}
                      </Col>
                    </Row>
                    <BuildLogsTable
                      repositoryId={data.id}
                      showWarnings={true}
                    />
                  </>
                )}
              </CustomModalWrapper>
            </Col>
          </Row>
        </Fragment>
      )}
    </CustomModalWrapper>
  );
};

export default RepositoryDetailModalContent;
